import React, { useState, useEffect, useRef } from "react";
import { Mail, Menu, X } from "lucide-react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, Polyline } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./App.css";
import logo from "./assets/logo.png";

// ── Utility: count up animation ──────────────────────────────────────────────
function useCountUp(target, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    const num = parseFloat(target);
    const isFloat = String(target).includes(".");
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = isFloat
        ? (eased * num).toFixed(1)
        : Math.floor(eased * num);
      setCount(current);
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

// ── Stat card with count-up ───────────────────────────────────────────────────
function StatCard({ num, suffix, label, started }) {
  const count = useCountUp(num, 1600, started);
  return (
    <div className="stat-card">
      <div className="stat-shimmer" />
      <div className="stat-number">{count}{suffix}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

// ── 3D Tilt Card wrapper (spring physics) ────────────────────────────────────
function TiltCard({ children, className = "" }) {
  const ref = useRef(null);
  const raf = useRef(null);
  const target = useRef({ x: 0, y: 0 });
  const current = useRef({ x: 0, y: 0 });

  const animate = () => {
    const el = ref.current;
    if (!el) return;
    current.current.x += (target.current.x - current.current.x) * 0.12;
    current.current.y += (target.current.y - current.current.y) * 0.12;
    el.style.transform = `perspective(900px) rotateX(${current.current.y}deg) rotateY(${current.current.x}deg) translateZ(10px)`;
    raf.current = requestAnimationFrame(animate);
  };

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    target.current.x = ((e.clientX - rect.left) / rect.width - 0.5) * 18;
    target.current.y = ((e.clientY - rect.top) / rect.height - 0.5) * -18;
  };

  const handleEnter = () => { raf.current = requestAnimationFrame(animate); };
  const handleLeave = () => {
    cancelAnimationFrame(raf.current);
    target.current = { x: 0, y: 0 };
    if (ref.current)
      ref.current.style.transform = "perspective(900px) rotateX(0deg) rotateY(0deg) translateZ(0)";
  };

  return (
    <div
      ref={ref}
      className={`tilt-card ${className}`}
      onMouseMove={handleMove}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {children}
    </div>
  );
}

// ── Floating particles for CTA ────────────────────────────────────────────────
function FloatingParticles({ count = 18 }) {
  return (
    <div className="fp-wrap" aria-hidden="true">
      {[...Array(count)].map((_, i) => (
        <div
          key={i}
          className="fp"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 6}s`,
            animationDuration: `${4 + Math.random() * 6}s`,
            width: `${4 + Math.random() * 6}px`,
            height: `${4 + Math.random() * 6}px`,
            opacity: 0.15 + Math.random() * 0.25,
          }}
        />
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
const ShyamVahanWebsite = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [statsStarted, setStatsStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const statsRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      const total = document.body.scrollHeight - window.innerHeight;
      setScrollProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll-triggered reveal with stagger
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // stagger children inside grid parents
            const siblings = entry.target.parentElement?.querySelectorAll(".reveal");
            if (siblings && siblings.length > 1) {
              siblings.forEach((el, i) => {
                setTimeout(() => el.classList.add("visible"), i * 100);
              });
            } else {
              entry.target.classList.add("visible");
            }
            if (entry.target === statsRef.current) setStatsStarted(true);
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [isLoading]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  if (isLoading) {
    return (
      <div className="loader">
        <div className="loader-box">
          <img src={logo} alt="SVL" className="loader-logo" />
          <h2 className="loader-name">Shyam Vahan Logistics</h2>
          <p className="loader-sub">PAN INDIA LOGISTICS SOLUTIONS</p>
          <div className="loader-bar"><div className="loader-fill" /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="site">
      {/* ── Scroll progress bar ── */}
      <div className="scroll-progress" style={{ width: `${scrollProgress}%` }} />

      {/* ── Navbar ── */}
      <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
        <div className="nav-inner">
          <img
            src={logo}
            alt="Shyam Vahan Logistics"
            className="nav-logo"
            onClick={() => scrollTo("hero")}
          />
          <div className="nav-links">
            {["About", "Services", "Network", "Clients"].map((item) => (
              <button key={item} className="nav-link" onClick={() => scrollTo(item.toLowerCase())}>
                {item}
              </button>
            ))}
            <button className="btn-quote" onClick={() => scrollTo("contact")}>
              Get a Quote
            </button>
          </div>
          <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {menuOpen && (
          <div className="mobile-nav">
            {["About", "Services", "Network", "Clients"].map((item) => (
              <button key={item} className="mobile-link" onClick={() => scrollTo(item.toLowerCase())}>
                {item}
              </button>
            ))}
            <button className="btn-quote mobile-quote" onClick={() => scrollTo("contact")}>
              Get a Quote
            </button>
          </div>
        )}
      </nav>

      {/* ── Hero ── */}
      <section id="hero" className="hero">
        {/* 3D floating boxes */}
        <div className="hero-3d-scene">
          <div className="cube cube-1"><div className="cube-face f"/><div className="cube-face b"/><div className="cube-face l"/><div className="cube-face r"/><div className="cube-face t"/><div className="cube-face bt"/></div>
          <div className="cube cube-2"><div className="cube-face f"/><div className="cube-face b"/><div className="cube-face l"/><div className="cube-face r"/><div className="cube-face t"/><div className="cube-face bt"/></div>
          <div className="cube cube-3"><div className="cube-face f"/><div className="cube-face b"/><div className="cube-face l"/><div className="cube-face r"/><div className="cube-face t"/><div className="cube-face bt"/></div>
        </div>

        <div className="hero-content reveal">
          <span className="hero-badge">PAN INDIA LOGISTICS SOLUTIONS</span>
          <h1 className="hero-heading">
            Reliable, Safe &<br />
            <span className="gold">Cost-Effective</span> Transport
          </h1>
          <p className="hero-desc">
            Shyam Vahan Logistics (OPC) Pvt. Ltd. — delivering end-to-end FTL,
            PTL, EXIM, Reefer &amp; ODC solutions across India with real-time
            visibility and guaranteed SLAs.
          </p>

          <div className="trusted-by">
            <span className="trusted-label">TRUSTED BY</span>
            <div className="trusted-logos">
              <span className="trust-badge vbl">VBL — Varun Beverages</span>
              <span className="trust-badge">Devyani International</span>
            </div>
          </div>

          <div className="hero-cta">
            <button className="btn-primary" onClick={() => scrollTo("contact")}>Get a Quote</button>
            <button className="btn-outline" onClick={() => scrollTo("about")}>Contact Us</button>
          </div>
        </div>

        <div className="hero-stats reveal" ref={statsRef}>
          <StatCard num={18} suffix="" label="Cities Covered Pan India" started={statsStarted} />
          <StatCard num={5} suffix="" label="Service Zones" started={statsStarted} />
          <StatCard num={4} suffix="+" label="Service Categories" started={statsStarted} />
          <StatCard num={2} suffix="" label="Major Enterprise Clients" started={statsStarted} />
          <div className="hubs-row">
            <span className="hub-label">PRIMARY OPERATION HUBS</span>
            <div className="hub-badges">
              <span className="hub-pill">Guwahati</span>
              <span className="hub-pill">Kolkata</span>
              <span className="hub-pill">Pathankot</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── About ── */}
      <section id="about" className="section about-section">
        <div className="container">
          <div className="section-tag reveal">WHO WE ARE</div>
          <h2 className="section-heading reveal">About Shyam Vahan Logistics</h2>
          <div className="about-grid">
            <div className="about-left reveal">
              <p className="about-p">
                Shyam Vahan Logistics (OPC) Private Limited is a fast-growing logistics
                company committed to delivering reliable, safe, and cost-effective
                transportation services across India.
              </p>
              <p className="about-p">
                With strong operational expertise and a dedicated team, we provide end-to-end
                logistics solutions including Full Truck Load (FTL), Part Truck Load (PTL),
                Containerised Movement (20ft &amp; 40ft), EXIM Transportation, and ODC
                Handling.
              </p>
              <p className="about-p">
                We specialise in building efficient logistics networks with key operational
                strength in <strong>Guwahati, Kolkata,</strong> and <strong>Pathankot</strong>,
                while ensuring seamless Pan India connectivity.
              </p>
              <p className="about-p">
                Our focus: transparent, timely, and customer-centric logistics solutions that
                help businesses optimise their supply chain.
              </p>
            </div>
            <div className="about-right">
              {[
                {
                  title: "Vision",
                  text: "Emerge as India's most trusted logistics partner, recognised for reliability and excellence in service delivery.",
                },
                {
                  title: "Mission",
                  text: "Deliver transparent, timely, and cost-effective logistics solutions fostering long-term client partnerships.",
                },
                {
                  title: "Commitment",
                  text: "Safety-first operations with real-time visibility, dedicated fleet services, and proactive customer communication.",
                },
              ].map((item) => (
                <TiltCard key={item.title} className="about-card reveal">
                  <h4 className="about-card-title">{item.title}</h4>
                  <p className="about-card-text">{item.text}</p>
                </TiltCard>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section id="services" className="section services-section">
        <div className="container">
          <div className="section-tag reveal">WHAT WE OFFER</div>
          <h2 className="section-heading reveal">Our Services</h2>
          <p className="section-sub reveal">
            Comprehensive transportation solutions designed for FMCG, QSR, Industrial and
            E-Commerce supply chains.
          </p>
          <div className="services-grid">
            {[
              {
                icon: "🚛",
                title: "Transportation Services",
                items: [
                  "Full Truck Load (FTL)",
                  "Part Truck Load (PTL)",
                  "Open & Closed Body",
                  "Containers 20ft & 40ft",
                  "Long-haul & Regional",
                ],
              },
              {
                icon: "⚙️",
                title: "Specialised Services",
                items: [
                  "Reefer Transportation",
                  "Over Dimensional Cargo (ODC)",
                  "EXIM (Import & Export)",
                  "Hazardous Cargo",
                  "Time-Bound Delivery",
                ],
              },
              {
                icon: "📦",
                title: "Distribution Services",
                items: [
                  "Primary Distribution",
                  "Secondary Distribution",
                  "Factory → Warehouse",
                  "Warehouse → Retail",
                  "Dedicated Fleet (Dry & Reefer)",
                ],
              },
              {
                icon: "📍",
                title: "Value-Added Services",
                items: [
                  "Real-time GPS Tracking",
                  "Route Planning & Optimisation",
                  "Vendor Coordination",
                  "Proactive Communication",
                  "SLA-based Service Levels",
                ],
              },
            ].map((svc, i) => (
              <TiltCard key={svc.title} className="service-card reveal">
                <div className="service-icon-anim">
                  <div className="service-icon-ring" />
                  <span className="service-icon-box">{svc.icon}</span>
                </div>
                <h3 className="service-title">{svc.title}</h3>
                <ul className="service-list">
                  {svc.items.map((it) => (
                    <li key={it}>{it}</li>
                  ))}
                </ul>
                <div className="card-shine" />
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* ── Network ── */}
      <section id="network" className="section network-section">
        <div className="container">
          <div className="section-tag reveal">WHERE WE OPERATE</div>
          <h2 className="section-heading reveal">Network &amp; Presence</h2>
          <p className="section-sub reveal">
            18 cities across 5 zones — with three primary origin hubs driving northeast, east
            and north India corridors.
          </p>
          <div className="network-grid">
            <div className="network-hubs reveal">
              {[
                { zone: "Northeast Hub", color: "#e85d04", cities: "Guwahati • Agartala • Shillong • Naharlagun" },
                { zone: "East Corridor", color: "#1a4d7e", cities: "Kolkata • Siliguri" },
                { zone: "North India", color: "#5e17eb", cities: "Delhi • Jammu • Jaipur • Dharamshala • Kanpur\nPrayagraj • Jaipur" },
                { zone: "South India", color: "#06d6a0", cities: "Chennai • Cochin" },
                { zone: "West India", color: "#d4af37", cities: "Mumbai • Ahmedabad" },
              ].map((z) => (
                <div key={z.zone} className="zone-row">
                  <div className="zone-dot" style={{ background: z.color }} />
                  <div>
                    <div className="zone-name">{z.zone}</div>
                    <div className="zone-cities">{z.cities}</div>
                  </div>
                </div>
              ))}
              <div className="hub-primary-row">
                <span className="hub-primary-label">Primary Hubs:</span>
                <span className="hub-pill dark">Guwahati</span>
                <span className="hub-pill dark">Kolkata</span>
                <span className="hub-pill dark">Pathankot</span>
              </div>
            </div>

            <div className="india-map-wrap reveal">
              <div className="map-title">PAN INDIA COVERAGE MAP</div>
              <div className="leaflet-wrap">
                <MapContainer
                  center={[22.5, 82]}
                  zoom={4.5}
                  scrollWheelZoom={false}
                  zoomControl={true}
                  style={{ height: "420px", width: "100%", borderRadius: "12px" }}
                  attributionControl={false}
                >
                  <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                    attribution=""
                  />

                  {/* Route lines */}
                  <Polyline
                    positions={[[26.14, 91.73], [22.57, 88.36]]}
                    pathOptions={{ color: "#e85d04", weight: 2, dashArray: "6 4", opacity: 0.7 }}
                  />
                  <Polyline
                    positions={[[32.27, 75.64], [28.61, 77.20]]}
                    pathOptions={{ color: "#5e17eb", weight: 2, dashArray: "6 4", opacity: 0.7 }}
                  />
                  <Polyline
                    positions={[[28.61, 77.20], [22.57, 88.36]]}
                    pathOptions={{ color: "#1a4d7e", weight: 2, dashArray: "6 4", opacity: 0.6 }}
                  />

                  {/* Primary hubs */}
                  {[
                    { pos: [26.14, 91.73], color: "#e85d04", label: "Guwahati", r: 10, primary: true },
                    { pos: [22.57, 88.36], color: "#1a4d7e", label: "Kolkata", r: 10, primary: true },
                    { pos: [32.27, 75.64], color: "#5e17eb", label: "Pathankot", r: 10, primary: true },
                  ].map((m) => (
                    <CircleMarker key={m.label} center={m.pos} radius={m.r} pathOptions={{ fillColor: m.color, fillOpacity: 0.95, color: "#fff", weight: 2 }}>
                      <Tooltip permanent direction="right" offset={[10, 0]} className="map-tooltip-primary">
                        <strong>{m.label}</strong>
                      </Tooltip>
                    </CircleMarker>
                  ))}

                  {/* Secondary cities */}
                  {[
                    { pos: [23.83, 91.28], label: "Agartala", color: "#e85d04" },
                    { pos: [25.57, 91.88], label: "Shillong", color: "#e85d04" },
                    { pos: [26.74, 93.13], label: "Naharlagun", color: "#e85d04" },
                    { pos: [26.72, 88.39], label: "Siliguri", color: "#1a4d7e" },
                    { pos: [28.61, 77.20], label: "Delhi", color: "#5e17eb" },
                    { pos: [32.73, 74.86], label: "Jammu", color: "#5e17eb" },
                    { pos: [26.85, 80.91], label: "Kanpur", color: "#5e17eb" },
                    { pos: [25.44, 81.84], label: "Prayagraj", color: "#5e17eb" },
                    { pos: [26.89, 75.80], label: "Jaipur", color: "#5e17eb" },
                    { pos: [32.10, 76.49], label: "Dharamshala", color: "#5e17eb" },
                    { pos: [13.08, 80.27], label: "Chennai", color: "#06d6a0" },
                    { pos: [9.93, 76.26],  label: "Cochin", color: "#06d6a0" },
                    { pos: [19.07, 72.87], label: "Mumbai", color: "#d4af37" },
                    { pos: [23.02, 72.57], label: "Ahmedabad", color: "#d4af37" },
                  ].map((m) => (
                    <CircleMarker key={m.label} center={m.pos} radius={6} pathOptions={{ fillColor: m.color, fillOpacity: 0.85, color: "#fff", weight: 1.5 }}>
                      <Tooltip direction="top" offset={[0, -6]}>{m.label}</Tooltip>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </div>

              <div className="map-legend">
                <span className="legend-item"><span className="legend-dot" style={{background:"#e85d04"}}/> Northeast</span>
                <span className="legend-item"><span className="legend-dot" style={{background:"#1a4d7e"}}/> East</span>
                <span className="legend-item"><span className="legend-dot" style={{background:"#5e17eb"}}/> North</span>
                <span className="legend-item"><span className="legend-dot" style={{background:"#06d6a0"}}/> South</span>
                <span className="legend-item"><span className="legend-dot" style={{background:"#d4af37"}}/> West</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Clients ── */}
      <section id="clients" className="section clients-section">
        <div className="container">
          <div className="section-tag reveal">OUR CLIENTS</div>
          <h2 className="section-heading reveal">Trusted Partners</h2>
          <p className="section-sub reveal">
            Proud to serve leading companies in FMCG and QSR supply chains with reliable,
            efficient logistics.
          </p>
          <div className="clients-grid">
            <TiltCard className="client-card reveal">
              <div className="client-logo-box vbl-box">VBL</div>
              <div className="client-info">
                <h3 className="client-name">Varun Beverages Limited</h3>
                <span className="client-badge active">✓ Active Partner</span>
                <p className="client-desc">
                  One of India's largest beverage manufacturers and PepsiCo's key bottling
                  partner. SVL provides comprehensive FTL &amp; PTL transportation for their
                  beverage supply chain across India and Northeast India corridors.
                </p>
                <div className="client-tags">
                  <div className="client-tag"><span className="tag-label">Coverage</span><strong>Pan India</strong></div>
                  <div className="client-tag"><span className="tag-label">Service</span><strong>FTL / PTL</strong></div>
                  <div className="client-tag"><span className="tag-label">Status</span><strong>Active</strong></div>
                </div>
                <div className="client-verified">✓ Verified &amp; Active Client — Pan India Delivery Network</div>
              </div>
            </TiltCard>

            <TiltCard className="client-card reveal">
              <div className="client-logo-box di-box">DI</div>
              <div className="client-info">
                <h3 className="client-name">Devyani International</h3>
                <span className="client-badge active">✓ Active Partner</span>
                <p className="client-desc">
                  A leading QSR operator running KFC, Pizza Hut &amp; Coca Cola Coffee franchises
                  across India. SVL provides comprehensive FTL &amp; PTL transportation for their
                  cold chain (reefer) and ambient logistics requirements, ensuring freshness and
                  on-time delivery.
                </p>
                <div className="client-tags">
                  <div className="client-tag"><span className="tag-label">Coverage</span><strong>Pan India</strong></div>
                  <div className="client-tag"><span className="tag-label">Service</span><strong>Reefer / PTL</strong></div>
                  <div className="client-tag"><span className="tag-label">Status</span><strong>Active</strong></div>
                </div>
                <div className="client-verified">✓ Verified &amp; Active Client — Pan India Delivery Network</div>
              </div>
            </TiltCard>
          </div>
        </div>
      </section>

      {/* ── Value Proposition ── */}
      <section className="section value-section">
        <div className="container">
          <div className="section-tag reveal">WHY CHOOSE US</div>
          <h2 className="section-heading reveal">Our Value Proposition</h2>
          <p className="section-sub reveal">Six pillars that set Shyam Vahan Logistics apart.</p>
          <div className="value-grid">
            {[
              { n: "01", title: "Vehicle Availability", desc: "Nationwide access to FTL, PTL, EXIM, Reefer &amp; ODC cargo categories — anytime, anywhere in India." },
              { n: "02", title: "Competitive Pricing", desc: "Leverages strategic long-haul routes including Guwahati, Kolkata and Pathankot corridors." },
              { n: "03", title: "Real-Time Visibility", desc: "GPS-enabled tracking with live vehicle and cargo status updates at every stage of transit." },
              { n: "04", title: "Time-Guaranteed SLAs", desc: "Punctual and predictable deliveries with consistent delivery timelines and proactive communication." },
              { n: "05", title: "Transparent Operations", desc: "End-to-end coordination with complete operational clarity and regular updates throughout the journey." },
              { n: "06", title: "Dedicated Fleet", desc: "Exclusive fleet reserved for key clients — dry &amp; reefer options — ensuring maximum reliability." },
            ].map((v) => (
              <TiltCard key={v.n} className="value-card reveal">
                <div className="value-num">{v.n}</div>
                <h4 className="value-title">{v.title}</h4>
                <p className="value-desc" dangerouslySetInnerHTML={{ __html: v.desc }} />
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section id="contact" className="cta-section">
        <FloatingParticles count={20} />
        <div className="cta-inner reveal">
          <h2 className="cta-heading">Ready to Optimise Your Logistics?</h2>
          <p className="cta-sub">
            Tell us more about your supply chain requirements — we'll design a dedicated freight
            offering for you.
          </p>
          <div className="cta-contact-row">
            <div className="cta-contact-item">
              <span className="cta-contact-icon">✉</span>
              <a href="mailto:sales@shyamvahanlogistics.com" className="cta-contact-link">
                sales@shyamvahanlogistics.com
              </a>
            </div>
            <div className="cta-contact-item">
              <span className="cta-contact-icon">📍</span>
              <span className="cta-contact-link">
                N No-104, Moh Kanoon Goyan, Gangoh – 247341
              </span>
            </div>
          </div>
          <div className="cta-buttons">
            <a href="mailto:sales@shyamvahanlogistics.com" className="btn-primary">Get a Quote</a>
            <a href="mailto:sales@shyamvahanlogistics.com" className="btn-outline-light">Contact Us</a>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <img src={logo} alt="SVL" className="footer-logo" />
            <div>
              <div className="footer-name">Shyam Vahan Logistics (OPC) Private Limited</div>
              <div className="footer-tagline">Fast · Safe · Reliable · Pan India</div>
            </div>
          </div>
          <div className="footer-links">
            {["About", "Services", "Network", "Clients"].map((l) => (
              <button key={l} className="footer-link" onClick={() => scrollTo(l.toLowerCase())}>{l}</button>
            ))}
          </div>
          <div className="footer-contact">
            <a href="mailto:sales@shyamvahanlogistics.com" className="footer-contact-item">
              <Mail size={14} /> sales@shyamvahanlogistics.com
            </a>
            <div className="footer-contact-item">
              <span>📍</span> N No-104, Moh Kanoon Goyan, Gangoh – 247341
            </div>
            <a href="https://shyamvahanlogistics.com" className="footer-contact-item">
              🌐 www.shyamvahanlogistics.com
            </a>
          </div>
        </div>
        <div className="footer-copy">
          © 2026 Shyam Vahan Logistics (OPC) Private Limited · All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default ShyamVahanWebsite;
